import { HeadMetadata } from '@/components/HeadMetadata';
import { ibm } from '@/utils/fonts/fonts';
import {
  datetimeInLocaleLanguageAndTimezone,
  timeAgo,
} from '@/utils/timeFormatters';
import { TTrackData } from '@/utils/types/TTrackData';
import { GetStaticProps, Metadata, NextPage } from 'next';
import Image from 'next/image';
import React, { ReactElement } from 'react';
import useSWR from 'swr';
import RiFileDamageFill from '~icons/ri/file-damage-fill';
import RiLoader5Fill from '~icons/ri/loader-5-fill';
import RiArrowRightUpLine from '~icons/ri/arrow-right-up-line';
import Link from 'next/link';
import RootLayout from './RootLayout';
import { NextPageWithLayout } from './_app';

interface Props {
  siteUrl: string;
}

const StreamedMusic: NextPageWithLayout<Props> = props => {
  const { data, error } = useSWR<TTrackData[]>(
    'https://reviews-api.tijoe.net/v1/streamed-music',
    async (input: RequestInfo, init: RequestInit) => {
      const res = await fetch(input, init);
      return res.json();
    },
    { refreshInterval: 1000 * 10 }
  );

  if (error)
    return (
      <>
        <article className="mx-auto flex max-w-4xl justify-center">
          <p className="flex w-fit flex-row items-center gap-3 rounded-2xl border border-white/10 bg-[#151515]/50 p-3 px-5 text-sm">
            Ошибка загрузки данных о последних треках
          </p>
        </article>
      </>
    );

  if (!data)
    return (
      <>
        <article className="mx-auto flex max-w-4xl justify-center">
          <p className="flex w-fit flex-row items-center gap-3 rounded-2xl border border-white/10 bg-[#151515]/50 p-3 px-5 text-sm">
            Получение списка треков
            <RiLoader5Fill className="animate-spin" />
          </p>
        </article>
      </>
    );

  return (
    <>
      <HeadMetadata
        pageTitle="Музыка со стримов › Тиджой"
        pageDescription="Музыка со стримов Тиджоя."
        canonicalUrl={`${props.siteUrl}/streamed-music`}
        robots="index,follow"
      />
      <section>
        <ul className="mx-auto w-full cursor-pointer divide-y divide-white/5 overflow-hidden rounded-3xl bg-[#151515]/50">
          {data.map((track, index) => (
            <li
              key={index}
              onClick={() => window.open(track.url.lastfm, '_blank')}
              className="flex flex-row items-center justify-between transition-colors duration-300 ease-out md:hover:bg-[#282828]/50"
            >
              <div className="flex flex-row items-center gap-3 text-sm md:text-base">
                {track.images === null ? (
                  <div className="flex h-14 w-14 items-center justify-center md:h-16 md:w-16">
                    <RiFileDamageFill />
                  </div>
                ) : (
                  <Image
                    src={track.images.medium.url}
                    alt={`Обложка трека ${track.name} от исполнителя ${track.artist}`}
                    width={track.images.medium.dimensions.width}
                    height={track.images.medium.dimensions.height}
                    className="h-14 w-14 md:h-16 md:w-16"
                  />
                )}
                <div className="flex w-40 flex-col gap-0.5 py-3 leading-tight md:w-auto">
                  <p className="line-clamp-2">{track.name}</p>
                  <p className="line-clamp-1 text-xs text-zinc-400 md:text-sm">
                    {track.artist}
                  </p>
                </div>
              </div>
              <time
                dateTime={track.date.iso}
                style={ibm.style}
                className="mx-3 text-xs text-zinc-400 md:mx-5 md:text-sm"
                title={datetimeInLocaleLanguageAndTimezone(
                  new Date(track.date.iso)
                )}
              >
                {timeAgo(new Date(track.date.iso), new Date())}
              </time>
            </li>
          ))}
        </ul>
      </section>
      <Link
        href="https://www.last.fm/ru/user/TijoeMusic"
        target="_blank"
        className="mx-auto mt-5 flex w-fit flex-row items-center gap-5 rounded-3xl bg-[#151515]/50 p-3 px-5 text-sm transition-colors duration-300 ease-out md:text-base md:hover:bg-[#282828]/50"
      >
        <p>Больше треков на LastFm </p>
        <RiArrowRightUpLine className="text-lg" />
      </Link>
    </>
  );
};

StreamedMusic.getLayout = function getLayout(page: ReactElement) {
  return <RootLayout>{page}</RootLayout>;
};

export default StreamedMusic;

export const getStaticProps: GetStaticProps<Props> = async () => {
  return {
    props: {
      siteUrl: process.env['SITE_URL'] as string,
    },
  };
};
